// Vencimientos de todas las plantas de la cuenta (PP-23). Logica pura: junta
// lo que devolvio cada planta (documentos, tipos exigidos, nomina) y arma dos
// grupos, vencidos y por vencer, con el sujeto y las plantas afectadas.
//
// Un documento es global (ADR-014 §2): el mismo id aparece en cada planta
// donde esta su sujeto. Aca se junta por id. Una planta esta afectada si el
// sujeto esta en su nomina y ella exige ese tipo; un documento que ninguna
// planta exige no se lista, porque no le quita elegibilidad a nadie.
//
// El estado es el que derivo la API con la fecha operativa de cada planta
// (zona IANA de la planta). Si dos plantas en zonas distintas no coinciden
// en el dia del corte, vale el peor.
import type { Document, DocumentStatus, Driver, RequiredDocuments, SiteVehicle } from "@ucorp/contracts";
import { worstStatus } from "./documents.js";

/** Lo que el portal leyo de una planta para armar los vencimientos. */
export interface SiteExpiryInput {
  site: { siteId: string; name: string; providerId: string };
  /** Fecha operativa de la planta (zona IANA de la planta) con la que la API derivo los estados. */
  today: string;
  documents: readonly Document[];
  /** undefined: API anterior a contratos 0.6.40, se toma toda planta del sujeto como afectada. */
  requiredDocuments: RequiredDocuments | undefined;
  vehicles: readonly SiteVehicle[];
  drivers: readonly Driver[];
}

export type ExpiryGroup = "expired" | "expiring_soon";

export interface AffectedSite {
  siteId: string;
  name: string;
}

/** Como se nombra al sujeto: codigo de movil y patente o nombre. */
export interface ExpirySubject {
  type: "vehicle" | "driver";
  code: string;
  /** Patente del vehiculo o nombre del conductor. */
  detail: string;
}

export interface ExpiryEntry {
  documentId: string;
  documentType: string;
  expiresAt: string | null;
  status: ExpiryGroup;
  subject: ExpirySubject;
  sites: AffectedSite[];
  /**
   * Desde que planta se renueva: el documento es uno solo, la planta solo
   * nombra al sujeto y fija el path de la escritura.
   */
  renewVia: { siteId: string; providerId: string };
  /** Fecha operativa de la planta de renewVia, para "vence en N dias". */
  today: string;
}

export interface Expiries {
  expired: ExpiryEntry[];
  expiringSoon: ExpiryEntry[];
}

function requires(required: RequiredDocuments | undefined, doc: Document): boolean {
  if (required === undefined) return true;
  return required[doc.subject.type].includes(doc.type);
}

function subjectOf(input: SiteExpiryInput, doc: Document): ExpirySubject | undefined {
  const { providerId, id } = doc.subject;
  if (doc.subject.type === "vehicle") {
    const row = input.vehicles.find((v) => v.providerId === providerId && v.id === id && v.inRoster);
    return row === undefined ? undefined : { type: "vehicle", code: row.code, detail: row.plate };
  }
  const row = input.drivers.find((d) => d.providerId === providerId && d.id === id && d.inRoster);
  return row === undefined ? undefined : { type: "driver", code: row.code, detail: row.name };
}

interface Accumulated {
  entry: Omit<ExpiryEntry, "status">;
  statuses: DocumentStatus[];
}

/**
 * Vencidos y por vencer de todas las plantas leidas. Cada lista ordenada por
 * vencimiento ascendente (lo mas urgente arriba), y a igual fecha por tipo y
 * codigo para que el orden no dependa del orden de las respuestas.
 */
export function collectExpiries(inputs: readonly SiteExpiryInput[]): Expiries {
  const byDocument = new Map<string, Accumulated>();
  for (const input of inputs) {
    for (const doc of input.documents) {
      if (doc.subject.providerId !== input.site.providerId) continue;
      if (!requires(input.requiredDocuments, doc)) continue;
      const subject = subjectOf(input, doc);
      if (subject === undefined) continue;
      const site = { siteId: input.site.siteId, name: input.site.name };
      const found = byDocument.get(doc.id);
      if (found === undefined) {
        byDocument.set(doc.id, {
          entry: {
            documentId: doc.id,
            documentType: doc.type,
            expiresAt: doc.expiresAt,
            subject,
            sites: [site],
            renewVia: { siteId: input.site.siteId, providerId: input.site.providerId },
            today: input.today,
          },
          statuses: [doc.status],
        });
      } else {
        if (!found.entry.sites.some((s) => s.siteId === site.siteId)) found.entry.sites.push(site);
        found.statuses.push(doc.status);
      }
    }
  }

  const result: Expiries = { expired: [], expiringSoon: [] };
  for (const { entry, statuses } of byDocument.values()) {
    const status = worstStatus(statuses);
    if (status === "expired") result.expired.push({ ...entry, status });
    else if (status === "expiring_soon") result.expiringSoon.push({ ...entry, status });
  }
  for (const list of [result.expired, result.expiringSoon]) {
    list.sort(compareEntries);
    for (const entry of list) entry.sites.sort((a, b) => a.name.localeCompare(b.name, "es"));
  }
  return result;
}

function compareEntries(a: ExpiryEntry, b: ExpiryEntry): number {
  const byDate = (a.expiresAt ?? "") < (b.expiresAt ?? "") ? -1 : (a.expiresAt ?? "") > (b.expiresAt ?? "") ? 1 : 0;
  if (byDate !== 0) return byDate;
  if (a.documentType !== b.documentType) return a.documentType < b.documentType ? -1 : 1;
  return a.subject.code < b.subject.code ? -1 : a.subject.code > b.subject.code ? 1 : 0;
}

export type SettledResult<R> = { ok: true; value: R } | { ok: false; error: unknown };

/**
 * Corre `task` sobre cada item con a lo sumo `limit` en vuelo y devuelve los
 * resultados en el orden de entrada. Cada resultado dice si fallo: una planta
 * caida no tapa las demas.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<R>,
): Promise<SettledResult<R>[]> {
  const results: SettledResult<R>[] = new Array<SettledResult<R>>(items.length);
  let next = 0;
  async function worker(): Promise<void> {
    while (next < items.length) {
      const index = next;
      next += 1;
      try {
        results[index] = { ok: true, value: await task(items[index]!) };
      } catch (error) {
        results[index] = { ok: false, error };
      }
    }
  }
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, () => worker());
  await Promise.all(workers);
  return results;
}
